import React from "react";

/**
 * Schematic AVANT/APRÈS illustrations for solutions without real photos.
 * Two panels side-by-side (or stacked on mobile), styled to match the
 * collage aesthetic (orange AVANT / APRÈS labels).
 */

function Frame({ label, tone = "orange", children, caption }) {
  return (
    <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-[#e2d9ce] bg-[#FDFBF7]">
      <div
        className={`absolute top-3 left-3 z-10 font-mono-tech text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full shadow-sm ${
          tone === "orange"
            ? "bg-[#FF9800] text-[#0F172A]"
            : "bg-[#2D7A4D] text-[#FDFBF7]"
        }`}
      >
        {label}
      </div>
      <div className="absolute inset-0 flex items-center justify-center p-6">
        {children}
      </div>
      {caption && (
        <div className="absolute bottom-3 left-3 right-3 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#64748B] text-center">
          {caption}
        </div>
      )}
    </div>
  );
}

/* ────────── BATTERY ────────── */
function BatteryBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      {/* Ground */}
      <line x1="0" y1="200" x2="340" y2="200" stroke="#0F172A" strokeWidth="2" />
      {/* Sun */}
      <circle cx="60" cy="45" r="16" fill="#FF9800" />
      <g stroke="#FF9800" strokeWidth="2" strokeLinecap="round">
        <line x1="60" y1="20" x2="60" y2="12" />
        <line x1="35" y1="45" x2="27" y2="45" />
        <line x1="42" y1="27" x2="36" y2="21" />
        <line x1="78" y1="27" x2="84" y2="21" />
      </g>
      {/* House */}
      <polygon points="110,110 170,60 230,110" fill="#0F172A" opacity="0.15" />
      <rect x="110" y="110" width="120" height="80" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
      {/* Panels */}
      <g transform="translate(130,75) rotate(-32)">
        <rect x="0" y="0" width="55" height="30" fill="#1E88E5" stroke="#0F172A" strokeWidth="1.5" />
        <line x1="18" y1="0" x2="18" y2="30" stroke="#0F172A" strokeWidth="0.8" />
        <line x1="37" y1="0" x2="37" y2="30" stroke="#0F172A" strokeWidth="0.8" />
      </g>
      <rect x="150" y="150" width="20" height="40" fill="#2D7A4D" />
      {/* Grid (right side, pylon) */}
      <g transform="translate(275,120)">
        <line x1="15" y1="0" x2="15" y2="80" stroke="#0F172A" strokeWidth="2" />
        <line x1="0" y1="15" x2="30" y2="15" stroke="#0F172A" strokeWidth="2" />
        <line x1="5" y1="30" x2="25" y2="30" stroke="#0F172A" strokeWidth="2" />
      </g>
      {/* Arrow to grid (loss) */}
      <g>
        <path d="M235 130 Q 260 130 275 135" stroke="#E65100" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <polygon points="272,131 282,135 272,139" fill="#E65100" />
        <text x="245" y="123" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#E65100" fontWeight="700">
          Surplus
        </text>
        <text x="245" y="155" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#E65100">
          revente 6c/kWh
        </text>
      </g>
      {/* Night moon */}
      <g transform="translate(20,140)">
        <path d="M12 0 A 12 12 0 1 0 12 24 A 8 8 0 1 1 12 0" fill="#1E88E5" />
        <text x="0" y="42" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#E65100" fontWeight="700">
          Nuit : 100%
        </text>
        <text x="0" y="53" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#E65100" fontWeight="700">
          réseau (25c)
        </text>
      </g>
    </svg>
  );
}

function BatteryAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <line x1="0" y1="200" x2="340" y2="200" stroke="#0F172A" strokeWidth="2" />
      <circle cx="60" cy="45" r="16" fill="#FF9800" />
      <g stroke="#FF9800" strokeWidth="2" strokeLinecap="round">
        <line x1="60" y1="20" x2="60" y2="12" />
        <line x1="35" y1="45" x2="27" y2="45" />
        <line x1="42" y1="27" x2="36" y2="21" />
        <line x1="78" y1="27" x2="84" y2="21" />
      </g>
      {/* House */}
      <polygon points="90,110 150,60 210,110" fill="#0F172A" opacity="0.15" />
      <rect x="90" y="110" width="120" height="80" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
      <g transform="translate(110,75) rotate(-32)">
        <rect x="0" y="0" width="55" height="30" fill="#1E88E5" stroke="#0F172A" strokeWidth="1.5" />
        <line x1="18" y1="0" x2="18" y2="30" stroke="#0F172A" strokeWidth="0.8" />
        <line x1="37" y1="0" x2="37" y2="30" stroke="#0F172A" strokeWidth="0.8" />
      </g>
      <rect x="130" y="150" width="20" height="40" fill="#2D7A4D" />
      {/* Battery */}
      <g transform="translate(240,120)">
        <rect x="0" y="0" width="60" height="70" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
        <rect x="10" y="-6" width="10" height="8" fill="#0F172A" />
        <rect x="40" y="-6" width="10" height="8" fill="#0F172A" />
        <rect x="8" y="12" width="44" height="14" rx="2" fill="#e6f4ea" stroke="#2D7A4D" strokeWidth="1" />
        <rect x="10" y="14" width="30" height="10" rx="1" fill="#2D7A4D" />
        <path d="M28 40 L20 55 L28 55 L23 68 L38 50 L30 50 Z" fill="#FF9800" stroke="#0F172A" strokeWidth="1.2" strokeLinejoin="round" />
      </g>
      {/* Arrows house ⇄ battery */}
      <path d="M210 145 Q 225 145 240 148" stroke="#2D7A4D" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <polygon points="237,144 246,148 237,152" fill="#2D7A4D" />
      <text x="215" y="138" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#2D7A4D" fontWeight="700">
        Stock jour
      </text>
      {/* Night usage */}
      <g transform="translate(20,140)">
        <path d="M12 0 A 12 12 0 1 0 12 24 A 8 8 0 1 1 12 0" fill="#1E88E5" />
        <text x="0" y="42" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#2D7A4D" fontWeight="700">
          Nuit : 90%
        </text>
        <text x="0" y="53" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#2D7A4D" fontWeight="700">
          autoconso.
        </text>
      </g>
    </svg>
  );
}

/* ────────── WATER HEATER ────────── */
function WaterBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#F5F1EA" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Old cumulus */}
      <rect x="120" y="50" width="70" height="120" rx="4" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2.5" />
      <circle cx="155" cy="80" r="10" fill="#0F172A" />
      <text x="155" y="84" fontFamily="Fraunces, serif" fontSize="10" fill="#FDFBF7" textAnchor="middle" fontWeight="700">
        90°
      </text>
      <rect x="130" y="140" width="50" height="20" rx="2" fill="#e2d9ce" />
      {/* Rust spots */}
      <circle cx="140" cy="110" r="3" fill="#8B4513" opacity="0.6" />
      <circle cx="170" cy="125" r="2" fill="#8B4513" opacity="0.6" />
      {/* Plug + lightning */}
      <g transform="translate(210,80)">
        <rect x="0" y="0" width="24" height="30" rx="3" fill="#0F172A" />
        <line x1="8" y1="10" x2="8" y2="20" stroke="#FDFBF7" strokeWidth="2" />
        <line x1="16" y1="10" x2="16" y2="20" stroke="#FDFBF7" strokeWidth="2" />
        <path d="M35 5 L25 20 L33 20 L28 35 L45 18 L35 18 Z" fill="#E65100" stroke="#0F172A" strokeWidth="1.5" strokeLinejoin="round" />
      </g>
      {/* KWh label */}
      <g transform="translate(60,100)">
        <rect x="0" y="0" width="60" height="40" rx="6" fill="#E65100" />
        <text x="30" y="17" fontFamily="Fraunces, serif" fontSize="15" fill="#FDFBF7" textAnchor="middle" fontWeight="700">
          1 200
        </text>
        <text x="30" y="30" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#FDFBF7" textAnchor="middle">
          kWh/an
        </text>
      </g>
    </svg>
  );
}

function WaterAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#e6f4ea" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Thermodynamic tank */}
      <rect x="110" y="65" width="80" height="120" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2.5" />
      {/* Top unit (compressor) */}
      <rect x="105" y="45" width="90" height="30" rx="4" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
      <circle cx="130" cy="60" r="6" fill="none" stroke="#0F172A" strokeWidth="1.5" />
      <line x1="130" y1="60" x2="135" y2="55" stroke="#0F172A" strokeWidth="1.2" />
      {/* Display */}
      <rect x="130" y="90" width="40" height="20" rx="2" fill="#e6f4ea" stroke="#2D7A4D" strokeWidth="1.5" />
      <text x="150" y="104" fontFamily="Fraunces, serif" fontSize="10" fill="#0F172A" textAnchor="middle" fontWeight="700">
        55°
      </text>
      {/* COP badge */}
      <g transform="translate(140,150)">
        <rect x="0" y="0" width="24" height="18" rx="3" fill="#2D7A4D" />
        <text x="12" y="12" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#FDFBF7" textAnchor="middle" fontWeight="700">
          COP3
        </text>
      </g>
      {/* Air intake arrows (from left) */}
      <g stroke="#1E88E5" strokeWidth="2.5" strokeLinecap="round" fill="none">
        <path d="M50 60 L 95 60" />
        <path d="M90 55 L 100 60 L 90 65" fill="#1E88E5" />
        <path d="M50 80 L 100 80" />
        <path d="M95 75 L 105 80 L 95 85" fill="#1E88E5" />
      </g>
      <text x="45" y="40" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#1E88E5" fontWeight="700">
        Air ambiant (gratuit)
      </text>
      {/* Small electricity in */}
      <g transform="translate(210,60)">
        <path d="M20 5 L10 20 L18 20 L13 35 L30 18 L20 18 Z" fill="#FF9800" stroke="#0F172A" strokeWidth="1.5" strokeLinejoin="round" />
      </g>
      {/* KWh label */}
      <g transform="translate(220,110)">
        <rect x="0" y="0" width="60" height="40" rx="6" fill="#2D7A4D" />
        <text x="30" y="17" fontFamily="Fraunces, serif" fontSize="15" fill="#FDFBF7" textAnchor="middle" fontWeight="700">
          400
        </text>
        <text x="30" y="30" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#FDFBF7" textAnchor="middle">
          kWh/an
        </text>
      </g>
    </svg>
  );
}

/* ────────── REWARDS / AIDES ────────── */
function RewardsBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Confused person */}
      <circle cx="80" cy="90" r="18" fill="#FF9800" />
      <path d="M65 130 L 65 165 L 95 165 L 95 130 Z" fill="#0F172A" />
      {/* Stack of papers */}
      <g transform="translate(150,60)">
        <rect x="0" y="0" width="55" height="70" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1.5" transform="rotate(-6 27 35)" />
        <rect x="6" y="6" width="55" height="70" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1.5" />
        <rect x="12" y="12" width="55" height="70" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1.5" transform="rotate(4 40 40)" />
        <line x1="20" y1="24" x2="60" y2="24" stroke="#0F172A" strokeWidth="1" />
        <line x1="20" y1="34" x2="55" y2="34" stroke="#0F172A" strokeWidth="1" />
        <line x1="20" y1="44" x2="60" y2="44" stroke="#0F172A" strokeWidth="1" />
        <line x1="20" y1="54" x2="45" y2="54" stroke="#0F172A" strokeWidth="1" />
      </g>
      {/* Question marks */}
      <text x="240" y="80" fontFamily="Fraunces, serif" fontSize="42" fill="#E65100" fontWeight="700">
        ?
      </text>
      <text x="270" y="120" fontFamily="Fraunces, serif" fontSize="32" fill="#E65100" fontWeight="700">
        ?
      </text>
      <text x="60" y="55" fontFamily="Fraunces, serif" fontSize="28" fill="#E65100" fontWeight="700">
        ?
      </text>
      {/* Bottom text */}
      <text x="170" y="180" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#E65100" textAnchor="middle" fontWeight="700">
        DOSSIERS COMPLEXES
      </text>
    </svg>
  );
}

function RewardsAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#e6f4ea" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Smiling person */}
      <circle cx="80" cy="90" r="18" fill="#FF9800" />
      <path d="M72 92 Q 80 100 88 92" stroke="#0F172A" strokeWidth="2" fill="none" strokeLinecap="round" />
      <circle cx="74" cy="86" r="1.8" fill="#0F172A" />
      <circle cx="86" cy="86" r="1.8" fill="#0F172A" />
      <path d="M65 130 L 65 165 L 95 165 L 95 130 Z" fill="#0F172A" />
      {/* Signed contract */}
      <g transform="translate(140,55)">
        <rect x="0" y="0" width="70" height="85" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
        {/* Green check */}
        <circle cx="55" cy="15" r="10" fill="#2D7A4D" />
        <path d="M50 15 L 54 19 L 60 12" stroke="#FDFBF7" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="10" y1="35" x2="55" y2="35" stroke="#0F172A" strokeWidth="1" />
        <line x1="10" y1="45" x2="60" y2="45" stroke="#0F172A" strokeWidth="1" />
        <line x1="10" y1="55" x2="45" y2="55" stroke="#0F172A" strokeWidth="1" />
        <text x="10" y="75" fontFamily="Fraunces, serif" fontSize="13" fill="#2D7A4D" fontWeight="700">
          Aides OK
        </text>
      </g>
      {/* Money bag */}
      <g transform="translate(230,80)">
        <path d="M0 15 Q 5 5 25 5 Q 45 5 50 15 L 55 50 Q 25 65 -5 50 Z" fill="#FF9800" stroke="#0F172A" strokeWidth="2" strokeLinejoin="round" />
        <text x="25" y="38" fontFamily="Fraunces, serif" fontSize="20" fill="#0F172A" textAnchor="middle" fontWeight="700">
          €
        </text>
      </g>
      <text x="170" y="185" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#2D7A4D" textAnchor="middle" fontWeight="700">
        HMCD MONTE TOUT
      </text>
    </svg>
  );
}

/* ────────── DISCOUNT / MARKETPLACE ────────── */
function DiscountBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Big price tag */}
      <g transform="translate(85,60)">
        <path d="M0 0 L 100 0 L 130 45 L 100 90 L 0 90 Z" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="110" cy="45" r="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1.5" />
        <text x="55" y="55" fontFamily="Fraunces, serif" fontSize="26" fill="#E65100" textAnchor="middle" fontWeight="700">
          15 000 €
        </text>
      </g>
      <text x="170" y="180" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#E65100" textAnchor="middle" fontWeight="700">
        PRIX PUBLIC
      </text>
    </svg>
  );
}

function DiscountAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#e6f4ea" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Stroke-through old price */}
      <text x="90" y="65" fontFamily="Fraunces, serif" fontSize="18" fill="#64748B" fontWeight="500">
        15 000 €
      </text>
      <line x1="82" y1="62" x2="165" y2="55" stroke="#E65100" strokeWidth="2" />
      {/* Big price tag */}
      <g transform="translate(85,75)">
        <path d="M0 0 L 100 0 L 130 45 L 100 90 L 0 90 Z" fill="#FF9800" stroke="#0F172A" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="110" cy="45" r="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1.5" />
        <text x="55" y="55" fontFamily="Fraunces, serif" fontSize="26" fill="#0F172A" textAnchor="middle" fontWeight="700">
          11 700 €
        </text>
      </g>
      <g transform="translate(230,75)">
        <circle cx="30" cy="30" r="30" fill="#2D7A4D" />
        <text x="30" y="26" fontFamily="Fraunces, serif" fontSize="15" fill="#FDFBF7" textAnchor="middle" fontWeight="700">
          -22%
        </text>
        <text x="30" y="42" fontFamily="JetBrains Mono, monospace" fontSize="8" fill="#FDFBF7" textAnchor="middle">
          HMCD
        </text>
      </g>
    </svg>
  );
}

/* ────────── TRACKING / SUIVI ────────── */
function TrackBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Blank meter */}
      <g transform="translate(120,55)">
        <rect x="0" y="0" width="100" height="80" rx="6" fill="#F5F1EA" stroke="#0F172A" strokeWidth="2" />
        <text x="50" y="35" fontFamily="Fraunces, serif" fontSize="20" fill="#0F172A" textAnchor="middle" fontWeight="700">
          ??? kWh
        </text>
        <rect x="15" y="50" width="70" height="15" rx="3" fill="#e2d9ce" />
      </g>
      <text x="170" y="180" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#E65100" textAnchor="middle" fontWeight="700">
        AUCUNE VISIBILITÉ
      </text>
    </svg>
  );
}

function TrackAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#e6f4ea" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* Phone */}
      <g transform="translate(130,45)">
        <rect x="0" y="0" width="80" height="130" rx="10" fill="#0F172A" />
        <rect x="6" y="10" width="68" height="110" rx="4" fill="#FDFBF7" />
        {/* Chart */}
        <text x="40" y="30" fontFamily="Fraunces, serif" fontSize="14" fill="#0F172A" textAnchor="middle" fontWeight="700">
          8.2 kWh
        </text>
        <g fill="#2D7A4D">
          <rect x="12" y="55" width="8" height="20" />
          <rect x="24" y="45" width="8" height="30" />
          <rect x="36" y="35" width="8" height="40" />
          <rect x="48" y="40" width="8" height="35" />
          <rect x="60" y="30" width="8" height="45" fill="#FF9800" />
        </g>
        <rect x="12" y="95" width="56" height="4" rx="2" fill="#e2e8f0" />
        <rect x="12" y="95" width="42" height="4" rx="2" fill="#2D7A4D" />
        <rect x="12" y="105" width="56" height="4" rx="2" fill="#e2e8f0" />
        <rect x="12" y="105" width="30" height="4" rx="2" fill="#FF9800" />
      </g>
      <text x="170" y="185" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#2D7A4D" textAnchor="middle" fontWeight="700">
        SUIVI TEMPS RÉEL
      </text>
    </svg>
  );
}

/* ────────── BILLS / AUDIT ────────── */
function BillsBefore() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#FDFBF7" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      {/* House with heat leaks (red arrows out) */}
      <rect x="120" y="90" width="100" height="80" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
      <polygon points="115,90 170,55 225,90" fill="#0F172A" opacity="0.15" />
      <rect x="160" y="130" width="20" height="40" fill="#2D7A4D" />
      {/* Heat leaks */}
      <g stroke="#E65100" strokeWidth="2.5" strokeLinecap="round">
        <path d="M120 110 Q 100 110 90 105" fill="none" />
        <path d="M92 100 L82 105 L92 110" fill="none" />
        <path d="M120 140 Q 100 140 90 145" fill="none" />
        <path d="M92 140 L82 145 L92 150" fill="none" />
        <path d="M220 110 Q 240 110 250 105" fill="none" />
        <path d="M248 100 L258 105 L248 110" fill="none" />
      </g>
      {/* Wallet / euro leaking */}
      <text x="60" y="85" fontFamily="Fraunces, serif" fontSize="22" fill="#E65100" fontWeight="700">-€</text>
      <text x="260" y="85" fontFamily="Fraunces, serif" fontSize="22" fill="#E65100" fontWeight="700">-€</text>
      <text x="170" y="190" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#E65100" textAnchor="middle" fontWeight="700">
        MAISON ÉNERGIVORE
      </text>
    </svg>
  );
}

function BillsAfter() {
  return (
    <svg viewBox="0 0 340 220" className="w-full h-full">
      <rect x="30" y="30" width="280" height="160" rx="6" fill="#e6f4ea" stroke="#0F172A" strokeWidth="1" strokeDasharray="4 4" />
      <rect x="120" y="90" width="100" height="80" fill="#FDFBF7" stroke="#0F172A" strokeWidth="2" />
      {/* Insulation aura */}
      <rect x="112" y="82" width="116" height="96" fill="none" stroke="#FF9800" strokeWidth="3" strokeDasharray="5 3" opacity="0.7" />
      <polygon points="115,90 170,55 225,90" fill="#2D7A4D" />
      <rect x="160" y="130" width="20" height="40" fill="#2D7A4D" />
      {/* Thermostat */}
      <g transform="translate(148,105)">
        <circle cx="15" cy="15" r="12" fill="#0F172A" />
        <text x="15" y="19" fontFamily="Fraunces, serif" fontSize="10" fill="#FF9800" textAnchor="middle" fontWeight="700">21°</text>
      </g>
      <text x="170" y="190" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#2D7A4D" textAnchor="middle" fontWeight="700">
        AUDIT HMCD · OPTIMISÉE
      </text>
    </svg>
  );
}

/* Registry */
const REGISTRY = {
  battery: { before: BatteryBefore, after: BatteryAfter, cap: "Solaire seul vs solaire + batterie" },
  water: { before: WaterBefore, after: WaterAfter, cap: "Cumulus classique vs thermodynamique" },
  rewards: { before: RewardsBefore, after: RewardsAfter, cap: "Dossiers sans HMCD vs avec HMCD" },
  track: { before: TrackBefore, after: TrackAfter, cap: "Sans supervision vs app HMCD" },
  bills: { before: BillsBefore, after: BillsAfter, cap: "Maison énergivore vs audit HMCD" },
};

export default function SchematicBeforeAfter({ type }) {
  const R = REGISTRY[type];
  if (!R) return null;
  const Before = R.before;
  const After = R.after;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Frame label="AVANT" tone="orange" caption={R.cap}>
        <Before />
      </Frame>
      <Frame label="APRÈS" tone="green">
        <After />
      </Frame>
    </div>
  );
}
